import {
	createBodhiConversation,
	deleteBodhiConversation,
	fetchBodhiConversations,
	fetchBodhiMessages,
	sendBodhiMessage,
	streamBodhiMessage,
	suggestAlbumTags,
} from "@bodhistreams/api/src";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthMe } from "./useAuthQueries";

export const llmQueryKeys = {
	all: ["llm"],
	bodhi: () => [...llmQueryKeys.all, "bodhi"],
	conversations: () => [...llmQueryKeys.bodhi(), "conversations"],
	messages: (conversationId) => [
		...llmQueryKeys.bodhi(),
		"messages",
		conversationId,
	],
};

export const useBodhiConversations = (enabled = true) => {
	const { data: authUser } = useAuthMe();

	return useQuery({
		queryKey: llmQueryKeys.conversations(),
		queryFn: fetchBodhiConversations,
		enabled: enabled && !!authUser,
		staleTime: 30 * 1000,
		gcTime: 5 * 60 * 1000,
		retry: (failureCount, error) => {
			if (error?.response?.status === 401 || error?.response?.status === 403) {
				return false;
			}
			return failureCount < 2;
		},
	});
};

export const useBodhiMessages = (conversationId, enabled = true) => {
	const { data: authUser } = useAuthMe();

	return useQuery({
		queryKey: llmQueryKeys.messages(conversationId),
		queryFn: () => fetchBodhiMessages(conversationId),
		enabled: enabled && !!authUser && !!conversationId,
		staleTime: 15 * 1000,
		gcTime: 5 * 60 * 1000,
		retry: (failureCount, error) => {
			if (error?.response?.status === 401 || error?.response?.status === 403) {
				return false;
			}
			return failureCount < 2;
		},
	});
};

export const useCreateBodhiConversation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: createBodhiConversation,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: llmQueryKeys.conversations() });
		},
	});
};

/**
 * Delete a Bodhi conversation and its history. The conversation is dropped
 * from the cached list right away so the panel never re-selects it while the
 * list refetches.
 */
export const useDeleteBodhiConversation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: deleteBodhiConversation,
		onSuccess: (_data, conversationId) => {
			queryClient.removeQueries({
				queryKey: llmQueryKeys.messages(conversationId),
			});
			queryClient.setQueryData(llmQueryKeys.conversations(), (old) =>
				Array.isArray(old)
					? old.filter((conversation) => conversation.id !== conversationId)
					: old,
			);
			queryClient.invalidateQueries({ queryKey: llmQueryKeys.conversations() });
		},
	});
};

export const useSendBodhiMessage = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: sendBodhiMessage,
		onSuccess: (data) => {
			queryClient.setQueryData(
				llmQueryKeys.messages(data.conversation.id),
				(oldMessages = []) => [
					...oldMessages,
					data.user_message,
					data.assistant_message,
				],
			);
			queryClient.invalidateQueries({ queryKey: llmQueryKeys.conversations() });
		},
	});
};

export const useStreamBodhiMessage = () => {
	const queryClient = useQueryClient();

	return {
		queryClient,
		streamMessage: streamBodhiMessage,
	};
};

/**
 * Genre tags suggested for an album by Jev. Every tag comes back scored, best
 * first; `suggested` marks the ones to pre-select. Nothing is saved.
 */
export const useSuggestAlbumTags = () =>
	useMutation({
		mutationFn: suggestAlbumTags,
	});
